import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Box,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Slide,
  Switch,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  ShoppingBag as CarryoutIcon,
  LocalShipping as DeliveryIcon,
  Restaurant as DineInIcon,
  DriveEta as DriveThruIcon,
  Close as CloseIcon,
  EventBusy as ClosedIcon,
  Schedule as HoursIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimeInput } from './TimeInput';
import { PICKER_DATE_FORMAT } from '../../utils/formatTime';
import { format, addDays, isToday, isTomorrow } from 'date-fns';
import { AVAILABLE_OCCASIONS } from '../../constants';

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Quick date presets (Today/Tomorrow)
const getQuickDatePresets = () => {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  return [
    { name: 'Today', date: today, label: `Today` },
    { name: 'Tomorrow', date: tomorrow, label: `Tomorrow` },
  ];
};

export type OccasionType = 'CARRYOUT' | 'DELIVERY' | 'DINE_IN' | 'DRIVE_THRU';

export interface SpecialHoursEntry {
  id?: string;
  startDate: Date;
  endDate: Date;
  isClosed: boolean;
  openTime?: string;
  closeTime?: string;
  occasions: OccasionType[];
  /** @deprecated Use occasions array instead */
  occasion?: 'DELIVERY' | 'CARRYOUT' | 'DINE_IN';
}

const OCCASION_CONFIG: Record<OccasionType, { label: string; icon: React.ReactNode; shortLabel: string }> = {
  CARRYOUT: { label: 'Carryout/Delivery', shortLabel: 'Pickup', icon: <CarryoutIcon sx={{ fontSize: 16 }} /> },
  DELIVERY: { label: 'Delivery', shortLabel: 'Delivery', icon: <DeliveryIcon sx={{ fontSize: 16 }} /> },
  DINE_IN: { label: 'Dine In', shortLabel: 'Dine In', icon: <DineInIcon sx={{ fontSize: 16 }} /> },
  DRIVE_THRU: { label: 'Drive-Thru', shortLabel: 'Drive', icon: <DriveThruIcon sx={{ fontSize: 16 }} /> },
};

interface AddSpecialHoursDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (entry: SpecialHoursEntry) => void;
  existingEntry?: SpecialHoursEntry;
  use24Hour?: boolean;
  saving?: boolean;
  availableOccasions?: OccasionType[];
  defaultOccasions?: OccasionType[];
  fullScreen?: boolean;
}

// Calculate Easter Sunday (Anonymous Gregorian algorithm)
function getEasterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

// First Monday of (year, month) — 0-indexed month
function firstMondayOf(year: number, month: number): Date {
  const d = new Date(year, month, 1);
  const offset = (8 - d.getDay()) % 7; // getDay: 0=Sun..1=Mon
  return new Date(year, month, 1 + offset);
}

// Last Monday of (year, month) — 0-indexed month
function lastMondayOf(year: number, month: number): Date {
  const d = new Date(year, month + 1, 0); // last day of month
  const offset = (d.getDay() + 6) % 7; // days back to Monday
  return new Date(year, month, d.getDate() - offset);
}

// UK (+ IRL) holiday presets for Taco Bell UK stores.
// Dynamically computed per-year so rollover to next occurrence works automatically.
// `defaultClosed` reflects typical UK QSR behavior: Christmas Day is the only near-universal
// full closure; every other holiday is normally a reduced-hours day (open with modified times),
// so selecting the chip pre-toggles the correct default.
interface HolidayPreset {
  name: string;
  getDate: (year: number) => Date;
  defaultClosed: boolean;
}

const HOLIDAY_PRESETS: HolidayPreset[] = [
  // Winter holidays
  { name: 'Christmas Eve', getDate: (year: number) => new Date(year, 11, 24), defaultClosed: false },
  { name: 'Christmas Day', getDate: (year: number) => new Date(year, 11, 25), defaultClosed: true },
  { name: 'Boxing Day', getDate: (year: number) => new Date(year, 11, 26), defaultClosed: false },
  { name: "New Year's Eve", getDate: (year: number) => new Date(year, 11, 31), defaultClosed: false },
  { name: "New Year's Day", getDate: (year: number) => new Date(year, 0, 1), defaultClosed: false },
  // Easter weekend — UK QSRs typically open normal or lightly-reduced hours
  {
    name: 'Good Friday',
    getDate: (year: number) => {
      const e = getEasterDate(year);
      return new Date(e.getFullYear(), e.getMonth(), e.getDate() - 2);
    },
    defaultClosed: false,
  },
  {
    name: 'Easter Monday',
    getDate: (year: number) => {
      const e = getEasterDate(year);
      return new Date(e.getFullYear(), e.getMonth(), e.getDate() + 1);
    },
    defaultClosed: false,
  },
  // St Patrick's Day (IRL / NI)
  { name: "St Patrick's Day", getDate: (year: number) => new Date(year, 2, 17), defaultClosed: false },
  // UK bank holiday Mondays — QSRs normally open regular hours
  { name: 'Early May Bank Holiday', getDate: (year: number) => firstMondayOf(year, 4), defaultClosed: false },
  { name: 'Spring Bank Holiday', getDate: (year: number) => lastMondayOf(year, 4), defaultClosed: false },
  { name: 'Summer Bank Holiday', getDate: (year: number) => lastMondayOf(year, 7), defaultClosed: false },
];

export const AddSpecialHoursDialog: React.FC<AddSpecialHoursDialogProps> = ({
  open,
  onClose,
  onSave,
  existingEntry,
  use24Hour = true,
  saving = false,
  availableOccasions = AVAILABLE_OCCASIONS as OccasionType[],
  defaultOccasions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [startDate, setStartDate] = useState<Date | null>(existingEntry?.startDate || null);
  const [endDate, setEndDate] = useState<Date | null>(existingEntry?.endDate || null);
  const [isClosed, setIsClosed] = useState(existingEntry?.isClosed ?? true);
  const [openTime, setOpenTime] = useState(existingEntry?.openTime || '11:00');
  const [closeTime, setCloseTime] = useState(existingEntry?.closeTime || '18:00');
  const [selectedOccasions, setSelectedOccasions] = useState<OccasionType[]>(
    existingEntry?.occasions || defaultOccasions || availableOccasions
  );
  const [error, setError] = useState<string | null>(null);

  const handleOccasionToggle = (occasion: OccasionType) => {
    setSelectedOccasions(prev => {
      if (prev.includes(occasion)) {
        if (prev.length === 1) return prev;
        return prev.filter(o => o !== occasion);
      } else {
        return [...prev, occasion];
      }
    });
  };

  useEffect(() => {
    if (existingEntry) {
      setStartDate(existingEntry.startDate || null);
      setEndDate(existingEntry.endDate || null);
      setIsClosed(existingEntry.isClosed ?? true);
      setOpenTime(existingEntry.openTime || '11:00');
      setCloseTime(existingEntry.closeTime || '18:00');
      setSelectedOccasions(existingEntry.occasions || availableOccasions);
    } else if (open) {
      setStartDate(null);
      setEndDate(null);
      setIsClosed(true);
      setOpenTime('11:00');
      setCloseTime('18:00');
      setSelectedOccasions(defaultOccasions || [...availableOccasions]);
    }
    setError(null);
  }, [existingEntry, open, availableOccasions, defaultOccasions]);

  const handlePresetClick = (preset: HolidayPreset) => {
    const now = new Date();
    let year = now.getFullYear();
    let holidayDate = preset.getDate(year);
    if (holidayDate < now) {
      year += 1;
      holidayDate = preset.getDate(year);
    }
    setStartDate(holidayDate);
    setEndDate(holidayDate);
    // Pre-select the typical UK QSR behavior for this holiday (Christmas Day = closed,
    // everything else = modified hours). Managers can still override.
    setIsClosed(preset.defaultClosed);
  };

  const handleSave = () => {
    setError(null);
    if (!startDate) {
      setError('Please select a date');
      return;
    }
    if (!endDate) {
      setError('Please select an end date');
      return;
    }
    if (endDate < startDate) {
      setError('End date must be after start date');
      return;
    }
    if (!isClosed && (!openTime || !closeTime)) {
      setError('Please set open and close times');
      return;
    }

    onSave({
      id: existingEntry?.id,
      startDate,
      endDate,
      isClosed,
      openTime: isClosed ? undefined : openTime,
      closeTime: isClosed ? undefined : closeTime,
      occasions: selectedOccasions,
    });
  };

  const handleClose = () => {
    if (saving) return;
    setError(null);
    setStartDate(null);
    setEndDate(null);
    setIsClosed(true);
    setOpenTime('11:00');
    setCloseTime('18:00');
    setSelectedOccasions([...availableOccasions]);
    onClose();
  };

  const quickPresets = getQuickDatePresets();
  const DatePickerComponent = isMobile ? MobileDatePicker : DatePicker;

  // Mobile Bottom Sheet
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            m: 0,
            maxHeight: '92vh',
            borderRadius: '16px 16px 0 0',
            width: '100%',
            maxWidth: '100%',
          },
        }}
        sx={{
          '& .MuiDialog-container': { alignItems: 'flex-end' },
          '& .MuiDialog-paper': { width: '100%', margin: 0 },
        }}
      >
        {/* Drag Handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 1 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'grey.300' }} />
        </Box>

        {/* Header */}
        <Box sx={{ px: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>
            {existingEntry ? 'Edit Special Hours' : 'Add Special Hours'}
          </Typography>
          <Button onClick={handleClose} sx={{ minWidth: 0, p: 0.5 }} disabled={saving}>
            <CloseIcon />
          </Button>
        </Box>

        {/* Content */}
        <DialogContent sx={{ px: 2.5, pt: 0, pb: 2, overflowY: 'auto' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={2.5}>
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Quick Select */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block', letterSpacing: 0.5 }}>
                  QUICK SELECT
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {quickPresets.map((preset) => {
                    const isSelected = startDate && (
                      (preset.name === 'Today' && isToday(startDate)) ||
                      (preset.name === 'Tomorrow' && isTomorrow(startDate))
                    );
                    return (
                      <Chip
                        key={preset.name}
                        label={preset.label}
                        onClick={() => {
                          setStartDate(preset.date);
                          setEndDate(preset.date);
                        }}
                        variant={isSelected ? 'filled' : 'outlined'}
                        color={isSelected ? 'primary' : 'default'}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    );
                  })}
                  {HOLIDAY_PRESETS.map((preset) => (
                    <Chip
                      key={preset.name}
                      label={preset.name}
                      onClick={() => handlePresetClick(preset)}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Christmas Day is typically the only full closure for UK stores. Other holidays default to modified hours — adjust as needed.
                </Typography>
              </Box>

              {/* Date Selection */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block', letterSpacing: 0.5 }}>
                  DATE
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DatePickerComponent
                    format={PICKER_DATE_FORMAT}
                    value={startDate}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                      if (!endDate || (newValue && endDate < newValue)) {
                        setEndDate(newValue);
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'Start',
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      }
                    }}
                    minDate={new Date()}
                  />
                  <Typography color="text.disabled" sx={{ px: 0.5 }}>→</Typography>
                  <DatePickerComponent
                    format={PICKER_DATE_FORMAT}
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'End',
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      }
                    }}
                    minDate={startDate || new Date()}
                  />
                </Stack>
              </Box>

              {/* Hours Type */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block', letterSpacing: 0.5 }}>
                  HOURS
                </Typography>
                <Stack spacing={1}>
                  <Box
                    onClick={() => setIsClosed(true)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: 1,
                      borderColor: isClosed ? 'error.main' : 'grey.300',
                      bgcolor: isClosed ? 'error.50' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <ClosedIcon sx={{ color: isClosed ? 'error.main' : 'grey.400', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={isClosed ? 600 : 500}>
                        Closed
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Store will be closed all day
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: 2,
                        borderColor: isClosed ? 'error.main' : 'grey.300',
                        bgcolor: isClosed ? 'error.main' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isClosed && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'white' }} />}
                    </Box>
                  </Box>

                  <Box
                    onClick={() => setIsClosed(false)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: 1,
                      borderColor: !isClosed ? 'primary.main' : 'grey.300',
                      bgcolor: !isClosed ? 'primary.50' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <HoursIcon sx={{ color: !isClosed ? 'primary.main' : 'grey.400', fontSize: 20 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={!isClosed ? 600 : 500}>
                          Modified Hours
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Custom open and close times
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          border: 2,
                          borderColor: !isClosed ? 'primary.main' : 'grey.300',
                          bgcolor: !isClosed ? 'primary.main' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {!isClosed && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'white' }} />}
                      </Box>
                    </Box>
                    
                    {!isClosed && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, ml: 4.5 }}>
                        <TimeInput value={openTime} onChange={setOpenTime} use24Hour={use24Hour} />
                        <Typography color="text.disabled">–</Typography>
                        <TimeInput value={closeTime} onChange={setCloseTime} use24Hour={use24Hour} />
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>

              {/* Occasions */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block', letterSpacing: 0.5 }}>
                  APPLY TO
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {availableOccasions.map((occasion) => {
                    const isSelected = selectedOccasions.includes(occasion);
                    return (
                      <Chip
                        key={occasion}
                        icon={OCCASION_CONFIG[occasion].icon as React.ReactElement}
                        label={OCCASION_CONFIG[occasion].shortLabel}
                        onClick={() => handleOccasionToggle(occasion)}
                        variant={isSelected ? 'filled' : 'outlined'}
                        color={isSelected ? 'primary' : 'default'}
                        size="small"
                        sx={{ 
                          fontWeight: isSelected ? 600 : 400,
                          '& .MuiChip-icon': { ml: 0.5 },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>

              {/* Summary Preview */}
              {startDate && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Special Hours
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(startDate, 'd MMM yyyy')}
                    {endDate && endDate.getTime() !== startDate.getTime() && (
                      <> – {format(endDate, 'd MMM yyyy')}</>
                    )}
                    {' • '}
                    {isClosed ? (
                      <span style={{ color: '#d32f2f' }}>Closed</span>
                    ) : (
                      <span>{openTime} – {closeTime}</span>
                    )}
                  </Typography>
                </Box>
              )}
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 2.5,
            pb: 'calc(env(safe-area-inset-bottom, 16px) + 16px)',
            pt: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            disabled={saving || !startDate}
            size="large"
            sx={{
              py: 1.5,
              borderRadius: 2,
              bgcolor: '#662C8B',
              '&:hover': { bgcolor: '#551e73' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : existingEntry ? 'Save Changes' : 'Add Special Hours'}
          </Button>
          <Button
            fullWidth
            onClick={handleClose}
            disabled={saving}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Desktop Dialog
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingEntry ? 'Edit Special Hours' : 'Add Special Hours'}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Quick Select
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {quickPresets.map((preset) => (
                  <Chip
                    key={preset.name}
                    label={`${preset.label} (${format(preset.date, 'd MMM')})`}
                    onClick={() => {
                      setStartDate(preset.date);
                      setEndDate(preset.date);
                    }}
                    variant="outlined"
                    size="small"
                  />
                ))}
                {HOLIDAY_PRESETS.map((preset) => (
                  <Chip
                    key={preset.name}
                    label={preset.name}
                    onClick={() => handlePresetClick(preset)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Christmas Day is typically the only full closure for UK stores. Other holidays default to modified hours — adjust as needed.
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <DatePicker
                format={PICKER_DATE_FORMAT}
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  if (!endDate || (newValue && endDate < newValue)) {
                    setEndDate(newValue);
                  }
                }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                minDate={new Date()}
              />
              <Typography color="text.secondary">to</Typography>
              <DatePicker
                format={PICKER_DATE_FORMAT}
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                minDate={startDate || new Date()}
              />
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Hours
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: isClosed ? 'error.50' : 'success.50',
                  border: 1,
                  borderColor: isClosed ? 'error.200' : 'success.200',
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {isClosed ? 'Closed All Day' : 'Modified Hours'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isClosed ? 'Store will be closed' : 'Set custom hours'}
                  </Typography>
                </Box>
                <Switch
                  checked={!isClosed}
                  onChange={(e) => setIsClosed(!e.target.checked)}
                  color="success"
                />
              </Box>
              {!isClosed && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <TimeInput value={openTime} onChange={setOpenTime} use24Hour={use24Hour} />
                  <Typography color="text.secondary">to</Typography>
                  <TimeInput value={closeTime} onChange={setCloseTime} use24Hour={use24Hour} />
                </Stack>
              )}
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Apply to Occasions
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {availableOccasions.map((occasion) => {
                  const isSelected = selectedOccasions.includes(occasion);
                  return (
                    <Chip
                      key={occasion}
                      icon={OCCASION_CONFIG[occasion].icon as React.ReactElement}
                      label={OCCASION_CONFIG[occasion].label}
                      onClick={() => handleOccasionToggle(occasion)}
                      variant={isSelected ? 'filled' : 'outlined'}
                      color={isSelected ? 'primary' : 'default'}
                      size="small"
                    />
                  );
                })}
              </Stack>
            </Box>

            {startDate && (
              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  <strong>Special Hours</strong>
                  {' — '}
                  {format(startDate, 'd MMM yyyy')}
                  {endDate && endDate.getTime() !== startDate.getTime() && (
                    <> to {format(endDate, 'd MMM yyyy')}</>
                  )}
                  {' — '}
                  {isClosed ? <span style={{ color: '#d32f2f' }}>Closed</span> : <span>{openTime} - {closeTime}</span>}
                </Typography>
              </Alert>
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{ bgcolor: '#662C8B', '&:hover': { bgcolor: '#551e73' } }}
        >
          {saving ? 'Saving...' : existingEntry ? 'Update' : 'Add Special Hours'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
